import { useCallback, useEffect, useMemo } from 'react';

import { Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useIsCompanyGoalsManagePage } from 'Deer/intent/Goals/components/MetaFilters';
import { useNavigateToGoalsClickThroughIndividualPage } from 'Deer/intent/Goals/hooks/useNavigateToGoalsClickThroughIndividualPage';
import {
  setSelectedCompaniesForGroupByIndividual,
  setSelectedLocationsForGroupByIndividual,
  useAllGoalsCompaniesForGroupByIndividual,
  useAllGoalsLocationsForGroupByIndividual,
  useSelectedGoalsCompaniesForGroupByIndividual,
  useSelectedGoalsLocationsForGroupByIndividual,
} from 'Deer/intent/Goals/slice/GoalsIntentSlice';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { startOfDay } from 'date-fns';
import { useDispatch } from 'react-redux';
import { setClickThroughView } from 'reducers/okrsPageSlice';
import { checkEnableCompanySupport, checkIntentLandingPagesFeatureEnabled } from 'utils/helpers/featureFlags';
import { areArraysEqual } from 'utils/helpers/utilsHelper';

import { OpenSidePanelWithoutPadding } from 'assets/assetsV1/iconComponents';

import { INPROGRESS_STATUS_COLOR_MAP, MODULE_KEYS_USER_PREFERENCE_PERSISTENCE } from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';
import { convertDateToString4, getPersistenceKey, notNullOrUndefined } from 'constants/helper';
import { localStorageKeys } from 'constants/localStorageKeys';
import { minifyNumber } from 'constants/numberMinifyHelper';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { Avatar } from 'uiToolkitV1/Avatar';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { TableBodyRow } from 'uiToolkitV1/TableComponents';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { StatusCell } from '../AgGridTreeView/components/CommonTreeCell/StatusCell';
import { CustomHeaderCell } from '../AgGridTreeView/components/CustomHeaderCell';
import { HeaderCellTooltip } from '../AgGridTreeView/components/HeaderCellTooltip';
import { LoadingOverlayComponent } from '../AgGridTreeView/components/LoadingOverlayComponent';
import { useGoalsInsightsGroupByIndividualData } from '../AgGridTreeView/hooks/dataHooks';
import {
  useSetCompanyMetaFilterInitialDataGoalsForGroupByIndividual,
  useSetLocationMetaFilterInitialDataGoalsForGroupByIndividual,
} from '../AgGridTreeView/hooks/metaFiltersHooks';
import { postProcessPopup } from '../AgGridTreeView/utils';

const defaultRowsPerPage = 10;

const CellContainer = styled(Flex)`
  height: 100%;
  align-items: center;
  justify-content: ${(props) => props.justify};
`;

function Company({ data }) {
  const { company } = data;
  const { companyName } = company ?? {};
  return (
    <CellContainer>
      <TextBodyMedium key={companyName} as="span">
        {companyName}
      </TextBodyMedium>
    </CellContainer>
  );
}

function EmpLocation({ data }) {
  const { empLocation } = data;
  return (
    <CellContainer>
      <TextBodyMedium key={empLocation} as="span">
        {empLocation}
      </TextBodyMedium>
    </CellContainer>
  );
}
const EmployeeNameComponent = ({ data }) => {
  const { imageUrl, designation, displayName } = data?.employeeDetails ?? {};
  return (
    <Flex justifyContent="flex-start" alignItems="center" h="100%">
      <ListItemComp
        withoutBackground
        isBoldText={false}
        title={displayName}
        LeftComponent={() => <Avatar src={imageUrl} />}
        subTitle={designation}
      />
    </Flex>
  );
};

const KeyResultCountComponent = ({ data }) => {
  const { keyResultCount = 0 } = data ?? {};

  return (
    <CellContainer justify="flex-end">
      <TextBodyMedium>{keyResultCount || 0}</TextBodyMedium>
    </CellContainer>
  );
};

const ChildrenObjectiveCountComponent = ({ data }) => {
  const { objectiveCount = 0 } = data ?? {};

  return (
    <CellContainer justify="flex-end">
      <TextBodyMedium>{objectiveCount || 0}</TextBodyMedium>
    </CellContainer>
  );
};

const LastCheckInComponent = ({ data }) => {
  const { lastCheckInDate } = data ?? {};

  return (
    <CellContainer justify="flex-start">
      {notNullOrUndefined(lastCheckInDate) ? (
        <TextBodyMedium>{convertDateToString4(lastCheckInDate)}</TextBodyMedium>
      ) : (
        <TextBodyMedium>Never</TextBodyMedium>
      )}
    </CellContainer>
  );
};

const ButtonContainer = styled('div')`
  display: none;
  visibility: hidden;
  right: 22px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
`;

const AverageProgressComponent = ({ data }) => {
  const { PROGRESS_LEVEL, COMPLETED_PERCENTAGE } = data?.status ?? {};

  return (
    <CellContainer justify="flex-end">
      {notNullOrUndefined(PROGRESS_LEVEL) && notNullOrUndefined(COMPLETED_PERCENTAGE) ? (
        <TextBodyMedium color={INPROGRESS_STATUS_COLOR_MAP[PROGRESS_LEVEL]?.textColor}>
          {`${minifyNumber(COMPLETED_PERCENTAGE)}%`}
        </TextBodyMedium>
      ) : (
        <TextBodyMedium>0</TextBodyMedium>
      )}
      {/* <ButtonContainer className="agGridShowOnHover">
        <Button className="hovering" variant={'rowButtonOutlined'} StartIcon={OpenSidePanel}>
          View Details
        </Button>
      </ButtonContainer> */}
    </CellContainer>
  );
};

const CheckinCountComponent = ({ data }) => {
  const { totalCheckIns = 0 } = data ?? {};

  return (
    <CellContainer justify="flex-end">
      {notNullOrUndefined(totalCheckIns) ? (
        <TextBodyMedium>{totalCheckIns}</TextBodyMedium>
      ) : (
        <TextBodyMedium>0</TextBodyMedium>
      )}
    </CellContainer>
  );
};

export const defaultRowsPerPageForGoals = {
  [localStorageKeys.rabbitGoalsInsightsTableRowsPerPage]: defaultRowsPerPage,
};

const GroupByIndividualInsightsTable = ({
  selectedTimeBoundPeriods,
  selectedCohort,
  selectedGroupByKey,
  selectedSplitByKey,
  gridRef,
  setChartData = () => {},
  onModalUpdated = () => {},
}) => {
  // const [rowsPerPageLS, setRowsPerPageLS] = useLocalStorage(
  //   localStorageKeys.rabbitGoalsInsightsTableRowsPerPage,
  //   defaultRowsPerPageForGoals
  // );

  const { navigateToGoalsClickThroughIndividualPage } = useNavigateToGoalsClickThroughIndividualPage();

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const gridApi = gridRef?.current?.api;

  const dispatch = useDispatch();

  const { data, isLoading, isError, status } = useGoalsInsightsGroupByIndividualData({
    cohort: selectedCohort,
    groupByKeys: selectedGroupByKey,
    splitByKeys: selectedSplitByKey,
    orderBy: null,
    timeBounds: selectedTimeBoundPeriods,
  });

  useSetCompanyMetaFilterInitialDataGoalsForGroupByIndividual(data);
  useSetLocationMetaFilterInitialDataGoalsForGroupByIndividual(data);

  useEffect(() => {
    setChartData((prev) => ({ ...prev, isLoading }));
    if (gridApi) {
      if (isLoading) {
        gridApi.setGridOption('loading', true);
      } else if (data && data.length === 0) {
        gridApi.showNoRowsOverlay();
      } else {
        gridApi.setGridOption('loading', false);
      }
    }
  }, [isLoading, data, gridApi]);

  const handleDataModelUpdated = useCallback(() => {
    const newRowData = [];
    gridRef?.current?.api?.forEachNodeAfterFilterAndSort((row) => {
      newRowData.push(row?.data);
    });
    setChartData((prev) => ({ ...prev, rowData: newRowData }));
  }, [gridRef, setChartData]);

  const isCompanyGoalsManagePage = useIsCompanyGoalsManagePage();

  const selectedCompanies = useSelectedGoalsCompaniesForGroupByIndividual();
  const allCompanies = useAllGoalsCompaniesForGroupByIndividual();
  const selectedLocations = useSelectedGoalsLocationsForGroupByIndividual();
  const allLocations = useAllGoalsLocationsForGroupByIndividual();

  const rowsData = useMemo(() => {
    const rowsDataBeforeFilters = data || [];

    if (checkEnableCompanySupport() && isCompanyGoalsManagePage) {
      if (gridApi) {
        gridApi.setColumnFilterModel('company', {
          filterType: 'multi',
          filterModels: [
            {
              filterType: 'set',
              values: selectedCompanies.map((s) => s.companyName),
            },
            null,
          ],
        });

        gridApi.setColumnFilterModel('empLocation', {
          filterType: 'multi',
          filterModels: [
            {
              filterType: 'set',
              values: selectedLocations.map((empLocation) => empLocation),
            },
            null,
          ],
        });

        gridApi.onFilterChanged(); // Refresh grid filtering
      }
    }

    return rowsDataBeforeFilters;
  }, [data, gridApi, isCompanyGoalsManagePage, selectedLocations, selectedCompanies]);

  useEffect(() => {
    const handleFilterChanged = () => {
      const filterModel = gridApi.getFilterModel();

      // some locations are unselected in ag-grid, update meta filter values if mismatched
      if (filterModel?.empLocation) {
        const filteredEmpLocations = filterModel?.empLocation?.filterModels?.[0]?.values || [];

        if (!areArraysEqual(filteredEmpLocations, selectedLocations)) {
          dispatch(setSelectedLocationsForGroupByIndividual(filteredEmpLocations));
        }
      } else if (filterModel?.company) {
        const filteredCompanies = filterModel?.company?.filterModels?.[0]?.values || [];

        if (
          !areArraysEqual(
            filteredCompanies,
            selectedCompanies.map((s) => s.companyName)
          )
        ) {
          dispatch(
            setSelectedCompaniesForGroupByIndividual(
              filteredCompanies.map((companyName) => {
                const company = allCompanies.find((s) => s.companyName === companyName);
                return company;
              })
            )
          );
        }
      }
      // all companies selected in ag-grid, update meta filter values
      else if (allCompanies.length !== selectedCompanies.length) {
        dispatch(setSelectedCompaniesForGroupByIndividual(allCompanies));
      }

      // all locations selected in ag-grid, update meta filter values
      else if (allLocations.length !== selectedLocations.length) {
        dispatch(setSelectedLocationsForGroupByIndividual(allLocations));
      }
    };

    if (gridApi) {
      gridApi.addEventListener('filterChanged', handleFilterChanged);
    }

    return () => {
      if (gridApi) {
        gridApi.removeEventListener('filterChanged', handleFilterChanged);
      }
    };
  }, [gridApi, allLocations, dispatch, selectedLocations, allCompanies, selectedCompanies]);

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: true,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: '',
    }),
    []
  );

  const getKeyResultTooltip = () =>
    `Shows the number of ${modifyString(
      keyResultsName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} in which the individual is an owner/collaborator`;

  const getNumberOfCheckInsTooltip = () =>
    `Shows the number of check-ins made by the individual on any of their ${modifyString(
      moduleName,
      StringModifier.PLURAL_SENTENCE_CASE
    )}`;

  const getLastCheckInTooltip = () =>
    `Shows the date of the most recent check-in made by the individual on any of the ${modifyString(
      moduleName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} in which they are an owner/collaborator`;

  const getAverageProgressTooltip = () =>
    `Shows the average progress across ${modifyString(
      keyResultsName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} where the individual is an owner/collaborator.`;

  const getObjectiveNumberTooltip = () =>
    `Shows the number of ${modifyString(
      objectivesName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} in which the individual is either an owner or collaborator`;

  const transformProgressStatusData = (statusObj) => {
    const progressCountObj = {};
    Object.keys(statusObj).forEach((statusKey) => {
      progressCountObj[statusKey] = {
        avgPercentage: statusObj[statusKey]?.COMPLETED_PERCENTAGE,
        count: statusObj[statusKey]?.COUNT,
      };
    });

    return progressCountObj;
  };

  const getStatusTooltip = () =>
    `Hover on the chart to visualise the status distribution of all the ${modifyString(
      keyResultsName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} for the individual`;

  const columnDefs = useMemo(
    () => [
      {
        colId: 'employee',
        field: 'employee',
        filter: 'agMultiColumnFilter',
        filterParams: {
          filters: [
            {
              filter: 'agSetColumnFilter',
              display: 'accordion',
              title: 'Filter by Function',
              filterParams: {
                valueFormatter: ({ value }) => {
                  if (value) return value;
                },
                getValue: ({ data }) =>
                  data?.employeeDetails?.employeeFunction ?? data?.employeeDetails?.department ?? '',
              },
            },
            {
              filter: 'agSetColumnFilter',
              title: 'Filter by Name',
              display: 'accordion',
              filterParams: { applyMiniFilterWhileTyping: true },
            },
          ],
        },
        suppressColumnsToolPanel: true,
        headerName: 'Employee',
        minWidth: 300,
        width: 300,
        flex: 1,
        valueGetter: (params) => params.data?.employeeDetails?.displayName,
        cellRenderer: EmployeeNameComponent,
      },
      {
        colId: 'gender',
        field: 'employee',
        headerName: 'Gender',
        valueGetter: (params) => params.data?.employeeDetails?.gender,
        hide: true,
        suppressColumnsToolPanel: true,
      },
      {
        colId: 'location',
        field: 'employee',
        headerName: 'Location',
        valueGetter: (params) => params.data?.employeeDetails?.location,
        hide: true,
        suppressColumnsToolPanel: true,
      },
      {
        colId: 'employee-function',
        field: 'employee',
        headerName: 'Function',
        valueGetter: (params) => params.data?.employeeDetails?.employeeFunction,
        hide: true,
        suppressColumnsToolPanel: true,
      },
      {
        colId: 'employee-role',
        field: 'employee',
        headerName: 'Role',
        valueGetter: (params) => params.data?.employeeDetails?.designation,
        hide: true,
        suppressColumnsToolPanel: true,
      },
      {
        colId: 'external-employee-id',
        field: 'employee',
        headerName: 'External employee ID',
        valueGetter: (params) => params.data?.employeeDetails?.externalEmpId,
        hide: true,
        suppressColumnsToolPanel: true,
      },
      {
        colId: 'manager-name',
        field: 'employee',
        headerName: 'Manager Name',
        valueGetter: (params) => params.data?.employeeDetails?.managerName,
        hide: true,
        suppressColumnsToolPanel: true,
      },
      {
        colId: 'manager-email',
        field: 'employee',
        headerName: 'Manager Email',
        valueGetter: (params) => params.data?.employeeDetails?.managerEmail,
        hide: true,
        suppressColumnsToolPanel: true,
      },
      {
        colId: 'kr-count',
        field: 'keyResultCount',
        filter: 'agNumberColumnFilter',
        headerName: `No. of ${modifyString(keyResultsName, StringModifier.PLURAL_TITLE_CASE)}`,
        headerTooltip: getKeyResultTooltip(),
        tooltipComponent: HeaderCellTooltip,
        tooltipComponentParams: { showColumnName: true },
        width: 120,
        type: 'numericColumn',
        filterValueGetter: (params) => params?.data?.keyResultCount ?? 0,
        valueGetter: (params) => params.data?.keyResultCount ?? 0,
        cellRenderer: KeyResultCountComponent,
      },
      {
        colId: 'checkin-count',
        field: 'checkinCount',
        headerName: 'No. of Check-ins',
        headerTooltip: getNumberOfCheckInsTooltip(),
        tooltipComponent: HeaderCellTooltip,
        tooltipComponentParams: { showColumnName: true },
        width: 120,
        filter: 'agNumberColumnFilter',
        type: 'numericColumn',
        filterValueGetter: (params) => params?.data?.totalCheckIns ?? 0,
        valueGetter: (params) => params?.data?.totalCheckIns ?? 0,
        cellRenderer: CheckinCountComponent,
      },
      {
        colId: 'table-last-checkin',
        field: 'lastCheckin',
        headerName: 'Last Check-in',
        headerTooltip: getLastCheckInTooltip(),
        tooltipComponent: HeaderCellTooltip,
        tooltipComponentParams: { showColumnName: true },
        width: 140,
        filter: 'agDateColumnFilter',
        filterValueGetter: (params) =>
          params?.data?.lastCheckInDate ? startOfDay(new Date(params?.data?.lastCheckInDate)) : null,
        valueGetter: (params) => (params?.data?.lastCheckInDate ? params?.data?.lastCheckInDate : null),
        cellRenderer: LastCheckInComponent,
      },
      {
        colId: 'average-progress',
        field: 'averageProgress',
        headerName: 'Average Progress',
        headerTooltip: getAverageProgressTooltip(),
        tooltipComponent: HeaderCellTooltip,
        tooltipComponentParams: { showColumnName: true },
        width: 140,
        filter: 'agNumberColumnFilter',
        type: 'numericColumn',
        filterParams: {
          applyMiniFilterWhileTyping: true,
          getValue: (params) => Number(params?.data?.status?.COMPLETED_PERCENTAGE?.toFixed(2) || 0),
          debounceMs: 200,
          filterOptions: ['equals', 'inRange'],
          suppressAndOrCondition: true,
        },
        valueGetter: (params) => Number(params?.data?.status?.COMPLETED_PERCENTAGE?.toFixed(2) || 0),
        cellRenderer: AverageProgressComponent,
      },
      {
        colId: 'table-status',
        field: 'progress',
        headerName: `${keyResultsName} Status`,
        width: 120,
        filter: 'agMultiColumnFilter',
        headerTooltip: getStatusTooltip(),
        tooltipComponent: HeaderCellTooltip,
        tooltipComponentParams: { showColumnName: true },
        sortable: false,
        headerClass: 'truncate-header-ag-grid',
        filterParams: {
          filters: [
            {
              filter: 'agNumberColumnFilter',
              display: 'accordion',
              title: 'On-Track count',
              filterParams: {
                applyMiniFilterWhileTyping: true,
                debounceMs: 200,
                suppressAndOrCondition: true,
                defaultOption: 'equals',
                defaultJoinOperator: 'AND',
                suppressMiniFilter: false,
                suppressRemoveEntries: false,
                suppressSelectAll: false,
                getValue: (data) => {
                  const { status = {} } = data?.data ?? {};
                  const progressCount = transformProgressStatusData(status);
                  return progressCount?.ON_TRACK?.count ?? 0;
                },
              },
            },
            {
              filter: 'agNumberColumnFilter',
              display: 'accordion',
              title: 'Off-Track count',
              filterParams: {
                applyMiniFilterWhileTyping: true,
                debounceMs: 200,
                suppressAndOrCondition: true,
                defaultOption: 'equals',
                defaultJoinOperator: 'AND',
                suppressMiniFilter: false,
                suppressRemoveEntries: false,
                suppressSelectAll: false,
                getValue: (data) => {
                  const { status = {} } = data?.data ?? {};
                  const progressCount = transformProgressStatusData(status);
                  return progressCount?.OFF_TRACK?.count ?? 0;
                },
              },
            },
            {
              filter: 'agNumberColumnFilter',
              display: 'accordion',
              title: 'At-Risk count',
              filterParams: {
                applyMiniFilterWhileTyping: true,
                debounceMs: 200,
                suppressAndOrCondition: true,
                defaultOption: 'equals',
                defaultJoinOperator: 'AND',
                suppressMiniFilter: false,
                suppressRemoveEntries: false,
                suppressSelectAll: false,
                getValue: (data) => {
                  const { status = {} } = data?.data ?? {};
                  const progressCount = transformProgressStatusData(status);
                  return progressCount?.AT_RISK?.count ?? 0;
                },
              },
            },
            {
              filter: 'agNumberColumnFilter',
              display: 'accordion',
              title: 'Closed count',
              filterParams: {
                applyMiniFilterWhileTyping: true,
                debounceMs: 200,
                suppressAndOrCondition: true,
                defaultOption: 'equals',
                defaultJoinOperator: 'AND',
                suppressMiniFilter: false,
                suppressRemoveEntries: false,
                suppressSelectAll: false,
                getValue: (data) => {
                  const { status = {} } = data?.data ?? {};
                  const progressCount = transformProgressStatusData(status);
                  return progressCount?.CLOSED?.count ?? 0;
                },
              },
            },
          ],
        },
        valueGetter: (params) => params?.progress?.progressCount ?? {},
        cellRenderer: ({ data }) => {
          const { status = {} } = data ?? {};
          const tooltipText = `There are no ${modifyString(
            keyResultsName,
            StringModifier.SENTENCE_CASE
          )} for which the user is an owner or collaborator, or they may have dependent tracking with no ${modifyString(
            keyResultsName,
            StringModifier.SENTENCE_CASE
          )} to derive status from.`;

          if (Object.keys(status).length === 0) {
            return (
              <Tooltip label={tooltipText}>
                <Flex alignItems="center" justifyContent="center" h="100%" w="100%">
                  <TextBodyMedium>NA</TextBodyMedium>
                </Flex>
              </Tooltip>
            );
          }
          return <StatusCell progress={{ progressCount: transformProgressStatusData(status) }} />;
        },
      },
      {
        colId: 'objective-count',
        field: 'objectiveCount',
        headerName: `No. of  ${modifyString(objectivesName, StringModifier.PLURAL_TITLE_CASE)}`,
        headerTooltip: getObjectiveNumberTooltip(),
        tooltipComponent: HeaderCellTooltip,
        tooltipComponentParams: { showColumnName: true },
        width: 120,
        filter: 'agNumberColumnFilter',
        type: 'numericColumn',
        hide: true,
        filterValueGetter: (params) => params?.data?.objectiveCount ?? 0,
        valueGetter: (params) => params.data?.objectiveCount ?? 0,
        cellRenderer: ChildrenObjectiveCountComponent,
      },
      {
        colId: 'company',
        field: 'company',
        order: 24,
        headerName: 'Company',
        cellRenderer: Company,
        hide: !checkEnableCompanySupport() || !isCompanyGoalsManagePage,
        suppressColumnsToolPanel: !checkEnableCompanySupport() || !isCompanyGoalsManagePage,
        minWidth: 150,
        filter: 'agSetColumnFilter',
        sortable: false,
        valueFormatter: (params) => params?.data?.company?.companyName || 'NA',
        filterParams: {
          getValue: (params) => params?.data?.company?.companyName || 'NA',
        },
      },
      {
        colId: 'empLocation',
        field: 'empLocation',
        order: 24,
        headerName: 'Location',
        cellRenderer: EmpLocation,
        hide: !checkEnableCompanySupport() || !isCompanyGoalsManagePage,
        suppressColumnsToolPanel: !checkEnableCompanySupport() || !isCompanyGoalsManagePage,
        minWidth: 200,
        filter: 'agSetColumnFilter',
        sortable: false,
        valueFormatter: (params) => params?.data?.empLocation || 'NA',
        filterParams: {
          getValue: (params) => params?.data?.empLocation || 'NA',
        },
        filterValueGetter: (params) => params?.data?.empLocation || 'NA',
      },
    ],
    [objectivesName, keyResultsName]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      menuTabs: ['filterMenuTab'],
      headerComponent: CustomHeaderCell,
    }),
    []
  );

  const getRowId = useCallback(({ data }) => data?.employeeDetails?.id, []);

  const rowClickHandler = ({ data, column }) => {
    if (column.colId === 'kebab-menu') {
    } else {
      const { id, displayName } = data?.employeeDetails ?? {};
      if (checkIntentLandingPagesFeatureEnabled()) {
        navigateToGoalsClickThroughIndividualPage({
          employeeId: id,
          name: displayName,
        });
      } else {
        dispatch(
          setClickThroughView({
            name: displayName,
            parentUuid: id,
          })
        );
      }
    }
  };

  const getRowStyle = () => ({ cursor: 'pointer' });

  /* Grid State persistence Key */

  const viewId = selectedCohort?.id;

  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.GOAL;
    const tabId = 'Insights';
    const uniqueId = 'GroupByIndividualGrid';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, [viewId]);

  const openDetailsPanel = useCallback(
    (data) => {
      const { id, displayName } = data?.employeeDetails ?? {};
      dispatch(
        setClickThroughView({
          name: displayName,
          parentUuid: id,
        })
      );
    },
    [dispatch]
  );

  return (
    <AgGridTable
      key={gridStatePersistenceKey} // we want to re-initialize on change of key
      gridStatePersistenceKey={gridStatePersistenceKey}
      ref={gridRef}
      rowData={rowsData}
      sideBar={sideBar}
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      // rowClass="ag-row-hide-on-hover"
      animateRows
      groupDefaultExpanded={-1}
      rowHeight={64}
      getRowStyle={getRowStyle}
      onCellClicked={rowClickHandler}
      getRowId={getRowId}
      suppressMenuHide
      postProcessPopup={postProcessPopup}
      loadingOverlayComponent={LoadingOverlayComponent}
      pagination
      paginationAutoPageSize
      tooltipShowDelay={500}
      onModelUpdated={() => {
        handleDataModelUpdated();
        onModalUpdated();
      }}
      kebabMenuIsRowSpecific
      kebabMenuOptionsGenerator={(data) => [
        {
          label: 'View Details',
          onClick: () => openDetailsPanel(data),
          disabled: false,
          Icon: OpenSidePanelWithoutPadding,
        },
      ]}
      className="insight-group-by-individual-ag-grid"
    />
  );
};

export const EmptyBar = styled.div`
  width: 30px;
  height: 8px;
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.neutral['999999']};
`;

export const StyledTableBodyRow = styled(TableBodyRow)`
  .action {
    display: none;
  }

  &:hover {
    cursor: pointer;
    & > .info:not(:first-child) {
      display: none;
    }

    // & > td:first-of-type .highlight-able-text {
    //   // font-weight: 600;
    //   color: ${(props) => props.theme.colors.text['1565BC']};
    // }

    & > .action {
      display: table-cell;
    }
  }
`;

export default GroupByIndividualInsightsTable;
